import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  BottomSheet,
  Fab,
  List,
  ListItem,
  Notice,
  Skeleton,
  iconAdd,
  Disclosure,
} from '@sauda/ui/kit';
import { Screen, useChrome } from '../../app/chrome.js';
import { LinkButton } from '../../app/LinkButton.js';
import { useAuth } from '../../auth/AuthContext.js';
import { ApiError, api } from '../../lib/api.js';
import type { Branch, Member } from '../../lib/types.js';
import { BranchForm, type BranchValues } from './BranchForm.js';

/** The branches of the agency: a row each with its area and people, archived ones apart. */
export function BranchesScreen() {
  const { t } = useTranslation();
  const auth = useAuth();
  const chrome = useChrome();
  const cache = useQueryClient();
  const canManage = auth.allowed('branches.manage') && !chrome.readOnly;
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planLimit, setPlanLimit] = useState(false);
  const branches = useQuery({
    queryKey: ['branches', auth.agencyId],
    queryFn: () => api<Branch[]>('/branches'),
  });
  const members = useQuery({
    queryKey: ['members', auth.agencyId],
    queryFn: () => api<Member[]>('/members'),
    enabled: auth.allowed('members.manage'),
  });
  const create = useMutation({
    mutationFn: (values: BranchValues) =>
      api<Branch>('/branches', { method: 'POST', body: values }),
    onSuccess: async () => {
      setOpen(false);
      setError(null);
      setPlanLimit(false);
      await cache.invalidateQueries({ queryKey: ['branches'] });
    },
    onError: (e) => {
      setPlanLimit(e instanceof ApiError && e.code === 'PLAN_LIMIT');
      setError(
        e instanceof ApiError && e.code === 'PLAN_LIMIT'
          ? t('branches.planLimit')
          : t('branches.generic'),
      );
    },
  });
  const all = branches.data ?? [];
  const active = all.filter((b) => b.archivedAt === null);
  const archived = all.filter((b) => b.archivedAt !== null);
  const people = (id: string) =>
    (members.data ?? []).filter((m) => m.branchId === id && m.status === 'active').length;
  return (
    <Screen title={t('branches.title')} large eyebrow={auth.current?.agencyName}>
      {error !== null && !open ? <Notice tone="error">{error}</Notice> : null}
      {branches.isPending ? <Skeleton height={72} /> : null}
      {active.length === 0 ? null : (
        <List label={t('branches.title')}>
          {active.map((b) => (
            <ListItem
              key={b.id}
              title={
                <Link className="row-link" to={`/branches/${b.id}`}>
                  {b.name}
                </Link>
              }
              supporting={[
                b.area,
                b.phone,
                members.data === undefined ? null : t('branches.people', { count: people(b.id) }),
              ]
                .filter(Boolean)
                .join(' · ')}
            />
          ))}
        </List>
      )}
      {archived.length === 0 ? null : (
        <Disclosure summary={t('branches.archivedGroup', { count: archived.length })}>
          <List label={t('branches.archived')}>
            {archived.map((b) => (
              <ListItem key={b.id} title={b.name} supporting={t('branches.archived')} />
            ))}
          </List>
        </Disclosure>
      )}
      {canManage ? (
        <div className="fab-slot">
          <Fab icon={iconAdd} label={t('branches.add')} extended onClick={() => setOpen(true)} />
        </div>
      ) : null}
      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        label={t('branches.add')}
        title={t('branches.add')}
        closeLabel={t('app.close')}
      >
        <div className="form-stack">
          {error === null ? null : <Notice tone="error">{error}</Notice>}
          {planLimit ? <LinkButton to="/billing">{t('branches.seePlans')}</LinkButton> : null}
          <BranchForm
            busy={create.isPending}
            submitLabel={t('branches.create')}
            onSubmit={(values) => create.mutate(values)}
          />
        </div>
      </BottomSheet>
    </Screen>
  );
}
